/**
 * Agent Hub — Tools Tab
 *
 * Tool management scoped to this chatbot.
 * Uses shadcn Dialog, AlertDialog, Badge, Switch, Label patterns.
 */

import React, { useEffect, useState, useCallback } from 'react'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Textarea } from '../../../components/ui/Textarea'
import { Select } from '../../../components/ui/Select'
import { Switch } from '../../../components/ui/switch'
import { Badge } from '../../../components/ui/Badge'
import { Card, CardContent } from '../../../components/ui/Card'
import { Label } from '../../../components/ui/label'
import { Separator } from '../../../components/ui/Separator'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog'
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../../../components/ui/alert-dialog'
import { toolsService, type ToolItem, type MCPServerTool, type ToolStatItem } from '../../../services/tools'
import { CustomHeadersEditor } from '../../../components/features/tools/CustomHeadersEditor'
import {
  splitHeadersTemplate, buildHeadersTemplate, type HeaderRow,
} from '../../../components/features/tools/headersTemplate'
import {
  Plus, Edit2, Trash2, Search, Loader2, Globe, Terminal,
  Wrench, AlertCircle, FileJson, ChevronDown, ChevronUp,
  Activity, Clock, CheckCircle2, XCircle, Key, Link2,
} from 'lucide-react'
import type { Chatbot } from '../../../types'

interface ToolsTabProps {
  chatbot: Chatbot
}

export const ToolsTab: React.FC<ToolsTabProps> = ({ chatbot }) => {
  // Extract variables from chatbot settings
  const settings = (chatbot.settings || {}) as Record<string, unknown>
  const variables = (settings.variables || {}) as Record<string, unknown>

  const [tools, setTools] = useState<ToolItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingTool, setEditingTool] = useState<ToolItem | null>(null)
  const [toolType, setToolType] = useState<'http' | 'mcp'>('http')

  // HTTP fields
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [useWhen, setUseWhen] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [pathTemplate, setPathTemplate] = useState('')
  const [method, setMethod] = useState<'GET' | 'POST'>('GET')
  const [token, setToken] = useState('')
  const [customHeaders, setCustomHeaders] = useState<HeaderRow[]>([])
  const [requiredFields, setRequiredFields] = useState('')
  const [optionalFields, setOptionalFields] = useState('')
  const [optionalDefaults, setOptionalDefaults] = useState('')
  const [fieldLabelsJson, setFieldLabelsJson] = useState('')
  const [fieldExamplesJson, setFieldExamplesJson] = useState('')

  // MCP fields
  const [mcpUrl, setMcpUrl] = useState('')
  const [mcpTransport, setMcpTransport] = useState<'http' | 'stdio'>('http')
  const [mcpToolPrefix, setMcpToolPrefix] = useState('')
  const [mcpCommand, setMcpCommand] = useState('')
  const [mcpArgs, setMcpArgs] = useState('')
  const [mcpEnv, setMcpEnv] = useState('')

  // MCP discovery
  const [isDiscovering, setIsDiscovering] = useState(false)
  const [discoveredTools, setDiscoveredTools] = useState<MCPServerTool[]>([])
  const [selectedToolNames, setSelectedToolNames] = useState<string[]>([])
  const [discoveryError, setDiscoveryError] = useState<string | null>(null)

  // Quick Import
  const [showQuickImport, setShowQuickImport] = useState(false)
  const [quickImportJson, setQuickImportJson] = useState('')
  const [quickImportError, setQuickImportError] = useState<string | null>(null)

  // Delete state
  const [isSaving, setIsSaving] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [toolToDelete, setToolToDelete] = useState<ToolItem | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Enable/disable toggling
  const [togglingId, setTogglingId] = useState<string | null>(null)

  // Stats & logs
  const [toolStats, setToolStats] = useState<Record<string, ToolStatItem>>({})
  const [expandedToolId, setExpandedToolId] = useState<string | null>(null)
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null)

  const loadStats = useCallback(async () => {
    try {
      const data = await toolsService.getToolStats(chatbot.id)
      const map: Record<string, ToolStatItem> = {}
      data.tools.forEach(s => { map[s.tool_id] = s })
      setToolStats(map)
    } catch {
      // Stats are non-critical, silently fail
    }
  }, [chatbot.id])

  const loadTools = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await toolsService.listBotTools(chatbot.id)
      setTools(data)
    } catch {
      try {
        const data = await toolsService.listTools()
        setTools(data.filter(t => t.type !== 'internal'))
      } catch (e2: unknown) {
        setError((e2 as Error)?.message || 'Failed to load tools')
      }
    } finally {
      setIsLoading(false)
    }
  }, [chatbot.id])

  useEffect(() => { loadTools(); loadStats() }, [loadTools, loadStats])

  const resetForm = () => {
    setName(''); setDescription(''); setUseWhen(''); setBaseUrl(''); setPathTemplate('')
    setMethod('GET'); setToken(''); setCustomHeaders([]); setRequiredFields(''); setOptionalFields(''); setOptionalDefaults('')
    setFieldLabelsJson(''); setFieldExamplesJson(''); setMcpUrl(''); setMcpTransport('http')
    setMcpToolPrefix(''); setMcpCommand(''); setMcpArgs(''); setMcpEnv('')
    setDiscoveredTools([]); setSelectedToolNames([]); setDiscoveryError(null); setEditingTool(null)
    setShowQuickImport(false); setQuickImportJson(''); setQuickImportError(null)
    setFormError(null)
  }

  const handleCreate = () => {
    resetForm(); setToolType('http'); setDialogOpen(true)
  }

  const handleEdit = (tool: ToolItem) => {
    resetForm()
    setEditingTool(tool); setToolType(tool.type as 'http' | 'mcp')
    setName(tool.name); setDescription(tool.description || ''); setUseWhen(tool.use_when || '')
    if (tool.type === 'http') {
      setBaseUrl(tool.base_url || ''); setPathTemplate(tool.path_template || '')
      setMethod((tool.method || 'GET') as 'GET' | 'POST')
      try {
        const { token: parsedToken, customHeaders: parsedHeaders } = splitHeadersTemplate(tool.headers_template)
        setToken(parsedToken)
        setCustomHeaders(parsedHeaders)
      } catch { setToken(''); setCustomHeaders([]) }
      try {
        const schemaObj = ((tool.method === 'POST' ? tool.body_schema : tool.query_schema) || {}) as Record<string, unknown>
        // Separate required vs optional fields, and extract _defaults/_labels/_examples
        const allKeys = Object.keys(schemaObj).filter(k => !k.startsWith('_'))
        const optKeys: string[] = []
        const reqKeys: string[] = []
        for (const k of allKeys) {
          const field = schemaObj[k] as Record<string, unknown> | undefined
          if (field && field.optional) optKeys.push(k)
          else reqKeys.push(k)
        }
        setRequiredFields(reqKeys.join(', '))
        setOptionalFields(optKeys.join(', '))
        if (schemaObj._defaults) setOptionalDefaults(JSON.stringify(schemaObj._defaults, null, 2))
        if (schemaObj._labels) setFieldLabelsJson(JSON.stringify(schemaObj._labels, null, 2))
        if (schemaObj._examples) setFieldExamplesJson(JSON.stringify(schemaObj._examples, null, 2))
      } catch { setRequiredFields('') }
    } else if (tool.type === 'mcp' && tool.mcp_config) {
      setMcpTransport(tool.mcp_config.transport || 'http'); setMcpUrl(tool.mcp_config.url || '')
      setMcpToolPrefix(tool.mcp_config.tool_prefix || ''); setMcpCommand(tool.mcp_config.command || '')
      setMcpArgs(tool.mcp_config.args?.join(', ') || ''); setMcpEnv(tool.mcp_config.env ? JSON.stringify(tool.mcp_config.env, null, 2) : '')
      if (tool.enabled_tool_names) setSelectedToolNames(tool.enabled_tool_names)
    }
    setDialogOpen(true)
  }

  const handleToggleEnabled = async (tool: ToolItem) => {
    setTogglingId(tool.id)
    try {
      await toolsService.setBotToolEnabled(chatbot.id, tool.id, !tool.enabled)
      await loadTools()
    } catch (e: unknown) {
      setError((e as Error)?.message || 'Failed to toggle tool')
    } finally {
      setTogglingId(null)
    }
  }

  const handleSave = async () => {
    try {
      setIsSaving(true); setError(null); setFormError(null)
      if (toolType === 'http') {
        if (!name || !baseUrl || !pathTemplate) { setFormError('Please fill in all required fields'); setIsSaving(false); return }
        const headers = buildHeadersTemplate(token, customHeaders)
        const reqFields = requiredFields.split(',').map(f => f.trim()).filter(Boolean)
        const optFields = optionalFields.split(',').map(f => f.trim()).filter(Boolean)
        const schema: Record<string, unknown> = {}
        reqFields.forEach(f => { schema[f] = { type: 'string' } })
        optFields.forEach(f => { schema[f] = { type: 'string', optional: true } })
        // Add defaults, labels, examples metadata
        try {
          const defaults = optionalDefaults.trim() ? JSON.parse(optionalDefaults) : {}
          if (defaults && Object.keys(defaults).length > 0) {
            schema['_defaults'] = defaults
            // Apply defaults to individual field defs
            for (const f of Object.keys(defaults)) {
              if (schema[f] && typeof schema[f] === 'object') (schema[f] as Record<string, unknown>).default = defaults[f]
            }
          }
          const labels = fieldLabelsJson.trim() ? JSON.parse(fieldLabelsJson) : undefined
          const examples = fieldExamplesJson.trim() ? JSON.parse(fieldExamplesJson) : undefined
          if (labels) schema['_labels'] = labels
          if (examples) schema['_examples'] = examples
        } catch { setFormError('Invalid JSON in Defaults, Labels, or Examples'); setIsSaving(false); return }
        const payload = { name, chatbot_id: chatbot.id, description: description || undefined, use_when: useWhen || undefined, base_url: baseUrl, path_template: pathTemplate, method, headers_template: headers, base_url_allowlist: [baseUrl], body_schema: method === 'POST' && Object.keys(schema).length > 0 ? schema : undefined, query_schema: method === 'GET' && Object.keys(schema).length > 0 ? schema : undefined }
        if (editingTool) await toolsService.updateHttpTool(editingTool.id, payload)
        else await toolsService.createHttpTool(payload)
      } else {
        if (!name) { setFormError('Please enter a tool name'); setIsSaving(false); return }
        if (mcpTransport === 'http' && !mcpUrl) { setFormError('Please enter MCP Server URL'); setIsSaving(false); return }
        if (mcpTransport === 'stdio' && !mcpCommand) { setFormError('Please enter command'); setIsSaving(false); return }
        const mcpConfig: Record<string, unknown> = { transport: mcpTransport, tool_prefix: mcpToolPrefix || undefined }
        if (mcpTransport === 'http') { mcpConfig.url = mcpUrl }
        else {
          mcpConfig.command = mcpCommand
          if (mcpArgs.trim()) mcpConfig.args = mcpArgs.split(',').map(a => a.trim()).filter(Boolean)
          if (mcpEnv.trim()) { try { mcpConfig.env = JSON.parse(mcpEnv) } catch { setFormError('Invalid JSON in env variables'); setIsSaving(false); return } }
        }
        const payload = { name, chatbot_id: chatbot.id, description: description || undefined, use_when: useWhen || undefined, mcp_config: mcpConfig, enabled_tool_names: selectedToolNames.length > 0 ? selectedToolNames : undefined }
        if (editingTool) await toolsService.updateMcpTool(editingTool.id, payload)
        else await toolsService.createMcpTool(payload)
      }
      await loadTools(); setDialogOpen(false); resetForm()
    } catch (e: unknown) {
      setFormError((e as Error)?.message || 'Failed to save tool')
    } finally { setIsSaving(false) }
  }

  const handleDiscoverTools = async () => {
    setIsDiscovering(true); setDiscoveryError(null)
    try {
      const mcpConfig: Record<string, unknown> = { transport: mcpTransport }
      if (mcpTransport === 'http') { if (!mcpUrl) { setDiscoveryError('Enter URL first'); return }; mcpConfig.url = mcpUrl }
      else { if (!mcpCommand) { setDiscoveryError('Enter command first'); return }; mcpConfig.command = mcpCommand; if (mcpArgs.trim()) mcpConfig.args = mcpArgs.split(',').map(a => a.trim()).filter(Boolean); if (mcpEnv.trim()) { try { mcpConfig.env = JSON.parse(mcpEnv) } catch { setDiscoveryError('Invalid JSON'); return } } }
      const response = await toolsService.listMcpServerTools(mcpConfig)
      setDiscoveredTools(response.tools)
      setSelectedToolNames(editingTool?.enabled_tool_names || response.tools.map(t => t.name))
    } catch (e: unknown) { setDiscoveryError((e as Error)?.message || 'Discovery failed'); setDiscoveredTools([]); setSelectedToolNames([]) }
    finally { setIsDiscovering(false) }
  }

  const handleQuickImport = () => {
    setQuickImportError(null)
    try {
      const config = JSON.parse(quickImportJson)
      if (!config.mcpServers || typeof config.mcpServers !== 'object') { setQuickImportError('Missing "mcpServers"'); return }
      const keys = Object.keys(config.mcpServers)
      if (keys.length === 0) { setQuickImportError('No servers found'); return }
      const key = keys[0]; const server = config.mcpServers[key]
      setName(key.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '))
      setToolType('mcp')
      if (server.url) { setMcpTransport('http'); setMcpUrl(server.url) }
      else if (server.command) { setMcpTransport('stdio'); setMcpCommand(server.command); if (Array.isArray(server.args)) setMcpArgs(server.args.join(', ')); if (server.env) setMcpEnv(JSON.stringify(server.env, null, 2)) }
      else { setQuickImportError('Invalid config'); return }
      setQuickImportJson(''); setShowQuickImport(false)
    } catch (e: unknown) { setQuickImportError(`Parse error: ${(e as Error).message}`) }
  }

  const handleConfirmDelete = async () => {
    if (!toolToDelete) return
    setIsDeleting(true)
    try {
      await toolsService.deleteTool(toolToDelete.id)
      await loadTools()
      setDeleteDialogOpen(false); setToolToDelete(null)
    } catch (e: unknown) { setError((e as Error)?.message || 'Failed to delete') }
    finally { setIsDeleting(false) }
  }

  // Separate system tools from user tools
  const systemTools = tools.filter(t => t.is_system || t.type === 'system')
  const userTools = tools.filter(t => !t.is_system && t.type !== 'system' && t.type !== 'internal')
  const skillTools = tools.filter(t => t.type === 'internal' && !t.is_system && t.skill_id)

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge variant="outline" size="sm">{tools.length} tool{tools.length !== 1 ? 's' : ''}</Badge>
          {userTools.filter(t => t.enabled !== false).length > 0 && (
            <span className="text-xs text-muted-foreground">
              {userTools.filter(t => t.enabled !== false).length} active
            </span>
          )}
        </div>
        <Button onClick={handleCreate} size="sm" variant="outline" className="gap-1.5">
          <Plus className="w-3.5 h-3.5" />
          <span>Add Tool</span>
        </Button>
      </div>

      {error && !dialogOpen && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* System tools */}
      {systemTools.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Built-in</p>
          {systemTools.map(tool => (
            <Card key={tool.id} className="transition-shadow hover:shadow-md">
              <CardContent className="flex items-center gap-3 p-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-md font-medium leading-none">{tool.name}</h3>
                  {tool.description && (
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span className="text-sm text-muted-foreground">{tool.description}</span>
                    </div>
                  )}
                </div>
                <Switch
                  checked={tool.enabled !== false}
                  onCheckedChange={() => handleToggleEnabled(tool)}
                  disabled={togglingId === tool.id}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Skill-created tools */}
      {skillTools.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">From Skills</p>
          {skillTools.map(tool => (
            <Card key={tool.id} className="transition-shadow hover:shadow-md">
              <CardContent className="flex items-center gap-3 p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-md font-medium leading-none">{tool.name}</h3>
                    <Badge variant="outline" className="font-normal">Skill</Badge>
                  </div>
                  {tool.description && (
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span className="text-sm text-muted-foreground">{tool.description}</span>
                    </div>
                  )}
                </div>
                <Switch
                  checked={tool.enabled !== false}
                  onCheckedChange={() => handleToggleEnabled(tool)}
                  disabled={togglingId === tool.id}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* User-created tools */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : userTools.length === 0 && systemTools.length === 0 && skillTools.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
            <Wrench className="w-6 h-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-foreground mb-1">No tools yet</p>
          <p className="text-xs text-muted-foreground max-w-xs">
            Connect HTTP APIs or MCP servers to give your agent the ability to take actions.
          </p>
        </div>
      ) : userTools.length > 0 ? (
        <div className="space-y-3">
          {(systemTools.length > 0 || skillTools.length > 0) && (
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Custom</p>
          )}
          {userTools.map(tool => {
            const stats = toolStats[tool.id]
            const isExpanded = expandedToolId === tool.id
            const successRate = stats && stats.total_calls > 0
              ? Math.round((stats.success_count / stats.total_calls) * 100)
              : null

            return (
              <Card key={tool.id} className="transition-shadow hover:shadow-md overflow-hidden">
                <CardContent className="flex items-start gap-3 p-4">
                  {/* Content */}
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex flex-col items-start justify-between space-y-1 lg:flex-row lg:space-y-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-md font-medium leading-none">{tool.name}</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-normal">{tool.type.toUpperCase()}</Badge>
                        {successRate !== null && (
                          <Badge variant={successRate >= 90 ? 'success' : successRate >= 70 ? 'warning' : 'error'}>
                            {successRate}%
                          </Badge>
                        )}
                      </div>
                    </div>

                    {tool.description && (
                      <p className="text-sm text-muted-foreground">{tool.description}</p>
                    )}

                    <div className="flex flex-wrap items-center gap-4">
                      {tool.type === 'http' && (
                        <Badge variant="outline" className="font-mono font-normal text-xs">
                          {tool.method} {tool.base_url}{tool.path_template}
                        </Badge>
                      )}
                      {tool.type === 'http' && tool.headers_template && (tool.headers_template as Record<string, string>).Authorization && (
                        <div className="text-muted-foreground flex items-center gap-1 text-xs">
                          <Key className="w-3 h-3" />
                          <span>Token set</span>
                        </div>
                      )}
                      {tool.type === 'mcp' && tool.mcp_config && (
                        <Badge variant="outline" className="font-mono font-normal text-xs">
                          {tool.mcp_config.transport === 'http' ? tool.mcp_config.url : `${tool.mcp_config.command} ${(tool.mcp_config.args || []).join(' ')}`}
                        </Badge>
                      )}

                      {stats && stats.total_calls > 0 && (
                        <>
                          <div className="text-muted-foreground flex items-center gap-1 text-xs">
                            <Activity className="w-3 h-3" />
                            <span>{stats.total_calls} call{stats.total_calls !== 1 ? 's' : ''}</span>
                          </div>
                          {stats.avg_latency_ms != null && (
                            <div className="text-muted-foreground flex items-center gap-1 text-xs">
                              <Clock className="w-3 h-3" />
                              <span>{stats.avg_latency_ms}ms</span>
                            </div>
                          )}
                          <button
                            onClick={() => setExpandedToolId(isExpanded ? null : tool.id)}
                            className="text-xs text-primary hover:underline"
                          >
                            {isExpanded ? 'Hide logs' : 'View logs'}
                          </button>
                        </>
                      )}

                      {tool.enabled_tool_names && tool.enabled_tool_names.length > 0 && (
                        <div className="text-muted-foreground flex items-center gap-1 text-xs">
                          <Wrench className="w-3 h-3" />
                          <span>{tool.enabled_tool_names.length} tools</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right side: toggle + actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <Switch
                      checked={tool.enabled !== false}
                      onCheckedChange={() => handleToggleEnabled(tool)}
                      disabled={togglingId === tool.id}
                    />
                    <button onClick={() => handleEdit(tool)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground" title="Edit">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => { setToolToDelete(tool); setDeleteDialogOpen(true) }} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-destructive" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </CardContent>

                {/* Expanded log panel */}
                {isExpanded && stats && stats.recent_calls.length > 0 && (
                  <div className="border-t bg-muted/30 px-4 py-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-muted-foreground">
                        Recent calls ({stats.recent_calls.length})
                      </p>
                      <button
                        onClick={() => setExpandedToolId(null)}
                        className="text-xs text-muted-foreground hover:text-foreground px-2 py-0.5 rounded hover:bg-muted"
                      >
                        Close
                      </button>
                    </div>
                    <div className="max-h-64 overflow-y-auto space-y-px">
                      {stats.recent_calls.map(call => {
                        const isCallExpanded = expandedCallId === call.id
                        const errorText = call.error && call.error !== 'None' ? call.error : null
                        const hasArgs = call.args && Object.keys(call.args).length > 0
                        const hasDetails = errorText || hasArgs
                        return (
                          <div key={call.id}>
                            <div
                              className={`flex items-center gap-2 text-xs py-1.5 px-2 rounded ${
                                hasDetails ? 'cursor-pointer hover:bg-muted/60' : ''
                              } ${isCallExpanded ? 'bg-muted/60' : ''}`}
                              onClick={() => hasDetails && setExpandedCallId(isCallExpanded ? null : call.id)}
                            >
                              {call.status === 'success' ? (
                                <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                              ) : (
                                <XCircle className="w-3.5 h-3.5 text-destructive shrink-0" />
                              )}
                              <Badge
                                variant={call.status === 'success' ? 'success' : 'error'}
                                size="sm"
                              >
                                {call.status}
                              </Badge>
                              {call.latency_ms != null && (
                                <span className="text-muted-foreground tabular-nums">{call.latency_ms}ms</span>
                              )}
                              {errorText && !isCallExpanded && (
                                <span className="text-destructive truncate flex-1 max-w-[250px]">{errorText}</span>
                              )}
                              {call.created_at && (
                                <span className="text-muted-foreground/60 ml-auto shrink-0">
                                  {new Date(call.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                              {hasDetails && (
                                <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground shrink-0 transition-transform ${isCallExpanded ? 'rotate-180' : ''}`} />
                              )}
                            </div>
                            {isCallExpanded && hasDetails && (
                              <div className="ml-6 mt-1 mb-2 p-2.5 rounded-md bg-muted/80 border text-xs space-y-2">
                                {errorText && (
                                  <div>
                                    <span className="font-medium text-destructive">Error: </span>
                                    <span className="text-foreground">{errorText}</span>
                                  </div>
                                )}
                                {hasArgs && (
                                  <div>
                                    <span className="font-medium text-muted-foreground">
                                      {call.status === 'error' ? 'Args' : 'Params'}:
                                    </span>
                                    <pre className="mt-1 text-xs text-foreground/80 font-mono bg-background rounded p-2 overflow-x-auto max-h-32 overflow-y-auto">
                                      {JSON.stringify(call.args, null, 2)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      ) : null}

      {/* ─── Create / Edit Dialog ─── */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { resetForm() }; setDialogOpen(open) }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {editingTool ? 'Edit Tool' : 'Add Tool'}
            </DialogTitle>
            <DialogDescription>
              {toolType === 'http'
                ? 'Connect an external REST API your agent can call.'
                : 'Connect an MCP server to expose its tools to your agent.'}
            </DialogDescription>
          </DialogHeader>

          {formError && dialogOpen && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              <span className="text-sm text-destructive">{formError}</span>
            </div>
          )}

          <div className="space-y-6 py-2 overflow-y-auto flex-1 min-h-0 pr-1">
            {/* ─ Tool type selector ─ */}
            {!editingTool && (
              <div className="space-y-2">
                <Label>Tool Type</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setToolType('http')}
                    className={`relative flex flex-col items-center gap-2 rounded-xl border-2 p-4 text-center transition-all ${
                      toolType === 'http'
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-border hover:border-muted-foreground/30 hover:bg-muted/50'
                    }`}
                  >
                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                      toolType === 'http' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                    }`}>
                      <Globe className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">HTTP API</p>
                      <p className="text-xs text-muted-foreground mt-0.5">REST endpoint</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setToolType('mcp')}
                    className={`relative flex flex-col items-center gap-2 rounded-xl border-2 p-4 text-center transition-all ${
                      toolType === 'mcp'
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-border hover:border-muted-foreground/30 hover:bg-muted/50'
                    }`}
                  >
                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                      toolType === 'mcp' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                    }`}>
                      <Terminal className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">MCP Server</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Model Context Protocol</p>
                    </div>
                  </button>
                </div>
              </div>
            )}

            <Separator />

            {/* ─ Basic Info ─ */}
            <div className="space-y-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Basic Info</p>
              <div className="space-y-1.5">
                <Label htmlFor="tool-name">Name <span className="text-destructive">*</span></Label>
                <Input
                  id="tool-name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder={toolType === 'http' ? 'e.g. Track Order' : 'e.g. GitHub Integration'}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tool-desc">Description</Label>
                <Input
                  id="tool-desc"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Brief description of what this tool does"
                  helperText="Helps the AI understand the tool's purpose"
                  autoComplete="off"
                />
              </div>
            </div>

            <Separator />

            {/* ─ HTTP Configuration ─ */}
            {toolType === 'http' && (
              <div className="space-y-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Endpoint</p>
                <div className="grid grid-cols-[120px_1fr] gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="tool-method">Method <span className="text-destructive">*</span></Label>
                    <Select
                      value={method}
                      onChange={v => setMethod(v as 'GET' | 'POST')}
                      options={[{ value: 'GET', label: 'GET' }, { value: 'POST', label: 'POST' }]}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="tool-url">Base URL <span className="text-destructive">*</span></Label>
                    <Input
                      id="tool-url"
                      value={baseUrl}
                      onChange={e => setBaseUrl(e.target.value)}
                      placeholder="https://api.example.com"
                      startIcon={<Link2 className="w-4 h-4" />}
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="tool-path">Path Template <span className="text-destructive">*</span></Label>
                  <Input
                    id="tool-path"
                    value={pathTemplate}
                    onChange={e => setPathTemplate(e.target.value)}
                    placeholder="/v1/orders/{order_id}"
                    helperText="Use {param} for dynamic path segments"
                    className="font-mono"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="tool-token">Auth Token</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        id="tool-token"
                        value={token}
                        onChange={e => setToken(e.target.value)}
                        type={token.startsWith('{{') ? 'text' : 'password'}
                        placeholder="Bearer token or {{variable}}"
                        startIcon={<Key className="w-4 h-4" />}
                        autoComplete="new-password"
                      />
                    </div>
                    {variables && Object.keys(variables).length > 0 && (
                      <Select
                        value=""
                        onChange={(v) => { if (v) setToken(`{{${v}}}`) }}
                        options={[
                          { value: '', label: 'Use variable...' },
                          ...Object.entries(variables).map(([vName, value]) => {
                            const displayValue = typeof value === 'string' ? value : ((value as Record<string, string>).default || '')
                            return {
                              value: vName,
                              label: `{{${vName}}}${displayValue ? ` (${displayValue.slice(0, 20)})` : ''}`,
                            }
                          }),
                        ]}
                        className="w-44 shrink-0"
                      />
                    )}
                  </div>
                  {token.startsWith('{{') && (
                    <p className="text-xs text-primary">Using variable: {token}</p>
                  )}
                </div>
                <CustomHeadersEditor headers={customHeaders} onChange={setCustomHeaders} variables={variables} />
                <div className="space-y-1.5">
                  <Label htmlFor="tool-fields">Required Fields</Label>
                  <Input
                    id="tool-fields"
                    value={requiredFields}
                    onChange={e => setRequiredFields(e.target.value)}
                    placeholder="order_id, email"
                    helperText="Comma-separated. Uses variables if available, otherwise asks the user."
                    autoComplete="off"
                  />
                </div>

                <Separator />

                {/* Parameters section */}
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Parameters</p>

                <div className="space-y-1.5">
                  <Label htmlFor="tool-opt-fields">
                    {method === 'POST' ? 'Optional Body Fields' : 'Optional Query Params'}
                  </Label>
                  <Input
                    id="tool-opt-fields"
                    value={optionalFields}
                    onChange={e => setOptionalFields(e.target.value)}
                    placeholder="limit, offset, sort, status"
                    helperText="Comma-separated. AI uses but won't ask if missing."
                    autoComplete="off"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="tool-defaults">Param Defaults</Label>
                  <Textarea
                    id="tool-defaults"
                    value={optionalDefaults}
                    onChange={e => setOptionalDefaults(e.target.value)}
                    placeholder='{"limit": "35", "sort": "newest"}'
                    rows={2}
                    className="font-mono text-xs"
                  />
                  <p className="text-xs text-muted-foreground">JSON — default values the AI will use when not specified</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="tool-labels">Field Labels</Label>
                    <Textarea
                      id="tool-labels"
                      value={fieldLabelsJson}
                      onChange={e => setFieldLabelsJson(e.target.value)}
                      placeholder='{"limit": "number of results"}'
                      rows={3}
                      className="font-mono text-xs"
                    />
                    <p className="text-xs text-muted-foreground">Human-readable names for each field</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="tool-examples">Field Examples</Label>
                    <Textarea
                      id="tool-examples"
                      value={fieldExamplesJson}
                      onChange={e => setFieldExamplesJson(e.target.value)}
                      placeholder='{"sort": "oldest or newest"}'
                      rows={3}
                      className="font-mono text-xs"
                    />
                    <p className="text-xs text-muted-foreground">Example values shown to the AI</p>
                  </div>
                </div>
              </div>
            )}

            {/* ─ MCP Configuration ─ */}
            {toolType === 'mcp' && (
              <div className="space-y-4">
                {/* Quick Import */}
                <div>
                  <button
                    type="button"
                    onClick={() => setShowQuickImport(!showQuickImport)}
                    className="flex items-center gap-1.5 text-sm text-primary hover:underline font-medium"
                  >
                    <FileJson className="w-4 h-4" />
                    Quick Import from JSON
                    {showQuickImport ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                  {showQuickImport && (
                    <div className="border border-primary/20 rounded-xl p-4 bg-primary/5 mt-3 space-y-3">
                      <Textarea
                        value={quickImportJson}
                        onChange={e => setQuickImportJson(e.target.value)}
                        placeholder='{"mcpServers":{"my-tool":{"command":"npx","args":["-y","my-mcp"]}}}'
                        rows={4}
                        className="font-mono text-xs"
                      />
                      <div className="flex items-center justify-end">
                        <Button onClick={handleQuickImport} size="sm" disabled={!quickImportJson.trim()}>Import</Button>
                      </div>
                      {quickImportError && (
                        <div className="p-2 bg-destructive/10 border border-destructive/20 rounded-md text-xs text-destructive">{quickImportError}</div>
                      )}
                    </div>
                  )}
                </div>

                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Connection</p>

                <div className="space-y-1.5">
                  <Label htmlFor="mcp-transport">Transport <span className="text-destructive">*</span></Label>
                  <Select
                    value={mcpTransport}
                    onChange={v => setMcpTransport(v as 'http' | 'stdio')}
                    options={[{ value: 'http', label: 'HTTP (Streamable)' }, { value: 'stdio', label: 'Stdio (npx / command)' }]}
                  />
                </div>

                {mcpTransport === 'http' ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="mcp-url">MCP Server URL <span className="text-destructive">*</span></Label>
                    <Input
                      id="mcp-url"
                      value={mcpUrl}
                      onChange={e => setMcpUrl(e.target.value)}
                      placeholder="https://mcp.example.com/sse"
                      startIcon={<Link2 className="w-4 h-4" />}
                      autoComplete="off"
                    />
                  </div>
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="mcp-cmd">Command <span className="text-destructive">*</span></Label>
                      <Input
                        id="mcp-cmd"
                        value={mcpCommand}
                        onChange={e => setMcpCommand(e.target.value)}
                        placeholder="npx"
                        startIcon={<Terminal className="w-4 h-4" />}
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="mcp-args">Arguments</Label>
                      <Input
                        id="mcp-args"
                        value={mcpArgs}
                        onChange={e => setMcpArgs(e.target.value)}
                        placeholder="-y, @modelcontextprotocol/server-github"
                        helperText="Comma-separated"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="mcp-env">Environment Variables</Label>
                      <Textarea
                        id="mcp-env"
                        value={mcpEnv}
                        onChange={e => setMcpEnv(e.target.value)}
                        placeholder='{"GITHUB_TOKEN": "ghp_..."}'
                        rows={3}
                        className="font-mono text-xs"
                      />
                      <p className="text-xs text-muted-foreground">JSON object of key-value pairs</p>
                    </div>
                  </>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="mcp-prefix">Tool Prefix</Label>
                  <Input
                    id="mcp-prefix"
                    value={mcpToolPrefix}
                    onChange={e => setMcpToolPrefix(e.target.value)}
                    placeholder="github_"
                    helperText="Prefix added to all tool names from this server"
                    autoComplete="off"
                  />
                </div>

                {/* Tool Discovery */}
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Discover Tools</p>
                    <Button
                      onClick={handleDiscoverTools}
                      disabled={isDiscovering || (mcpTransport === 'http' ? !mcpUrl : !mcpCommand)}
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                    >
                      {isDiscovering ? <><Loader2 className="w-3 h-3 animate-spin" />Discovering...</> : <><Search className="w-3 h-3" />Discover</>}
                    </Button>
                  </div>

                  {discoveryError && (
                    <div className="p-2 bg-destructive/10 border border-destructive/20 rounded-md text-xs text-destructive">{discoveryError}</div>
                  )}

                  {discoveredTools.length > 0 && (
                    <div className="rounded-xl border bg-muted/30 p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-foreground">Found {discoveredTools.length} tools</span>
                        <div className="flex gap-3">
                          <button onClick={() => setSelectedToolNames(discoveredTools.map(t => t.name))} className="text-xs text-primary hover:underline">Select All</button>
                          <button onClick={() => setSelectedToolNames([])} className="text-xs text-muted-foreground hover:underline">Clear</button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5 max-h-60 overflow-y-auto">
                        {discoveredTools.map(dt => {
                          const isSelected = selectedToolNames.includes(dt.name)
                          return (
                            <label
                              key={dt.name}
                              className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                                isSelected ? 'bg-primary/5 border-primary/30' : 'bg-card border-border hover:border-muted-foreground/25'
                              }`}
                            >
                              <Switch
                                checked={isSelected}
                                onCheckedChange={() => setSelectedToolNames(prev =>
                                  prev.includes(dt.name) ? prev.filter(n => n !== dt.name) : [...prev, dt.name]
                                )}
                                className="mt-0.5 scale-75"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-foreground">{dt.name}</div>
                                {dt.description && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{dt.description}</div>}
                              </div>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <Separator />

            {/* ─ AI Instructions ─ */}
            <div className="space-y-1.5">
              <Label htmlFor="tool-use-when">When to use</Label>
              <Textarea
                id="tool-use-when"
                value={useWhen}
                onChange={e => setUseWhen(e.target.value)}
                placeholder="Describe when the AI should invoke this tool..."
                rows={3}
              />
              <p className="text-xs text-muted-foreground">Tell the AI under what conditions to call this tool</p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0 border-t pt-4 shrink-0">
            <Button variant="ghost" onClick={() => { setDialogOpen(false); resetForm() }}>Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving || !name}>
              {isSaving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Saving...</> : <><Plus className="w-4 h-4 mr-1.5" />{editingTool ? 'Update' : 'Create'} Tool</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete AlertDialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tool</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{toolToDelete?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting} onClick={() => setToolToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Deleting...</> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default ToolsTab
